/**
 * Installing the widget on a store.
 *
 * Two paths, and the order matters: WordPress first, because that is the
 * supported one and it involves no copying of keys at all. The script tag is
 * the fallback, presented as such — a merchant who can install a plugin should
 * never end up hand-pasting a key they can get wrong.
 */

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";

import {
  useCreatePairingCode,
  useDeleteSite,
  useSite,
  useUpdateSite,
} from "../../api/queries";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";
import { StatusBadge } from "../SitesPage";

const WIDGET_URL =
  import.meta.env.VITE_WIDGET_URL ?? "https://bozorbot-widget.onrender.com/widget.js";

const PLUGIN_URL = "https://wordpress.org/plugins/bozor-bot/";

/** How often to look for the plugin having redeemed the code. */
const PAIRING_POLL_MS = 3000;

export function ConnectPage() {
  const { orgId, siteId } = useSiteContext();
  const pairing = useCreatePairingCode(orgId, siteId);

  // Poll only while a code is outstanding and the store has not connected.
  // Before that there is nothing to wait for; after it, nothing changes.
  const [awaitingPairing, setAwaitingPairing] = useState(false);
  const site = useSite(orgId, siteId, awaitingPairing ? PAIRING_POLL_MS : undefined);

  useEffect(() => {
    if (site.data?.status === "connected") setAwaitingPairing(false);
  }, [site.data?.status]);

  if (site.isLoading) return <Loading />;
  if (site.error) return <ErrorNote error={site.error} />;
  if (!site.data) return null;

  const snippet = [
    "<script",
    "  async",
    `  src="${WIDGET_URL}"`,
    `  data-site-key="${site.data.site_key}"`,
    "></script>",
  ].join("\n");

  return (
    <div className="space-y-4">
      <Card title="Connection status">
        <div className="flex items-center gap-3">
          <StatusBadge status={site.data.status} />
          <span className="text-sm text-muted">
            {site.data.last_seen_at
              ? `Last contact ${new Date(site.data.last_seen_at).toLocaleString()}`
              : "No contact from this store yet."}
          </span>
        </div>
      </Card>

      <Card title="WordPress and WooCommerce" action={<Badge tone="brand">Recommended</Badge>}>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>
            Install the{" "}
            <a href={PLUGIN_URL} target="_blank" rel="noopener noreferrer" className="underline">
              Bozor Bot plugin
            </a>{" "}
            from WordPress.org, or search for “Bozor Bot” under{" "}
            <strong>Plugins → Add New</strong> on your site.
          </li>
          <li>
            Open <strong>Settings → Bozor Bot</strong> and press{" "}
            <strong>Connect</strong>.
          </li>
          <li>
            Approve the connection here. The plugin stores its own credentials
            and adds the widget to your storefront — nothing to copy.
          </li>
        </ol>

        <div className="mt-4">
          <Button
            variant="primary"
            onClick={() => {
              setAwaitingPairing(true);
              pairing.mutate();
            }}
            disabled={pairing.isPending}
          >
            {pairing.isPending ? "Generating…" : "Generate a pairing code"}
          </Button>
        </div>

        {pairing.error != null && (
          <div className="mt-3">
            <ErrorNote error={pairing.error} />
          </div>
        )}

        {pairing.data && (
          <div className="mt-3 rounded-lg border border-line bg-surface p-3">
            <p className="text-xs text-muted">
              Paste this into the plugin. It expires{" "}
              {new Date(pairing.data.expires_at).toLocaleTimeString()}.
            </p>
            <code className="mt-1 block font-mono text-lg tracking-wider">
              {pairing.data.code}
            </code>

            {/* Announced as well as shown: the state changes without any
                action in this tab, so a screen-reader user gets no other
                signal that the wait is over. */}
            <p role="status" className="mt-2 text-xs text-muted">
              {site.data?.status === "connected"
                ? "Connected. Your store is paired."
                : "Waiting for your store to connect…"}
            </p>
          </div>
        )}
      </Card>

      <Card title="Any other platform">
        <p className="mb-3 text-sm text-muted">
          Paste this before the closing <code>&lt;/body&gt;</code> tag. The site
          key is public — it appears in the page source of every storefront that
          installs the widget, and it only works from the domains you allow
          below.
        </p>
        <CopyBlock text={snippet} />
        <p className="mt-3 text-sm text-muted">
          Then press <strong>Sync now</strong> on the Overview tab. Without the
          plugin we read your pages through your <code>sitemap.xml</code>, so
          stock and product variations are less reliable than on WordPress.
        </p>
      </Card>

      <AllowedOrigins orgId={orgId} siteId={siteId} origins={site.data.allowed_origins} />

      <DeleteSite orgId={orgId} siteId={siteId} name={site.data.name} />
    </div>
  );
}

/**
 * Removing the site from the account.
 *
 * It lives on the install tab because that is where the connection is managed,
 * and deleting the site is what ends it. Typing the name is the confirmation:
 * a plain "Are you sure?" gets clicked through by habit, and this takes the
 * catalogue, transcripts and Q&A answers with it, with no undo.
 */
function DeleteSite({ orgId, siteId, name }: { orgId: string; siteId: string; name: string }) {
  const remove = useDeleteSite(orgId, siteId);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const matches = typed.trim().toLowerCase() === name.trim().toLowerCase();

  function close() {
    setTyped("");
    remove.reset();
    setOpen(false);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!matches) return;
    remove.mutate(undefined, { onSuccess: () => void navigate({ to: "/sites" }) });
  }

  return (
    <Card title="Delete this site">
      <p className="mb-3 text-sm text-muted">
        Removes the site from your account with its synced catalogue,
        conversations and Q&amp;A answers. The widget stops answering and the
        WordPress plugin stops working until you remove it or connect it again.
      </p>
      <Button variant="danger" onClick={() => setOpen(true)}>
        Delete site
      </Button>

      <Modal open={open} onClose={close} title={`Delete ${name}`}>
        <form onSubmit={submit} className="space-y-4">
          {remove.error != null && <ErrorNote error={remove.error} />}

          <p className="text-sm text-muted">
            This permanently deletes the site and everything synced or recorded
            for it. It cannot be undone.
          </p>

          <Field label={`Type ${name} to confirm`} id="confirm-site-name">
            <Input
              id="confirm-site-name"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field>

          <div className="flex justify-end gap-2">
            <Button onClick={close}>Cancel</Button>
            <Button type="submit" variant="danger" disabled={!matches || remove.isPending}>
              {remove.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

function CopyBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg bg-surface p-3 text-xs">{text}</pre>
      <Button
        size="sm"
        className="absolute right-2 top-2"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // Clipboard access is denied in some browsers and over http. The
            // text is selectable either way, so this is not worth an error.
          }
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
      {/* Announced, because the button's label change is easy to miss. */}
      <span role="status" className="sr-only">
        {copied ? "Snippet copied to clipboard" : ""}
      </span>
    </div>
  );
}

/**
 * The origin allowlist.
 *
 * This is the control that makes a public site key safe: a copied key is
 * useless from any domain not listed here. Worth explaining on the screen,
 * because a customer who does not understand it is a customer who adds `*`.
 */
function AllowedOrigins({
  orgId,
  siteId,
  origins,
}: {
  orgId: string;
  siteId: string;
  origins: string[];
}) {
  const update = useUpdateSite(orgId, siteId);
  const [draft, setDraft] = useState("");

  function save(next: string[]) {
    update.mutate({ allowed_origins: next });
  }

  return (
    <Card title="Allowed domains">
      <p className="mb-3 text-sm text-muted">
        The widget only answers on these domains. This is what makes the public
        site key safe to put in your page source.
      </p>

      {update.error != null && <ErrorNote error={update.error} />}

      {origins.length === 0 ? (
        <p className="mb-3 text-sm text-warn">
          No domains added yet — the widget will not answer anywhere.
        </p>
      ) : (
        <ul className="mb-3 space-y-1">
          {origins.map((origin) => (
            <li key={origin} className="flex items-center justify-between gap-2 text-sm">
              <code>{origin}</code>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => save(origins.filter((item) => item !== origin))}
                aria-label={`Remove ${origin}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const value = draft.trim().replace(/\/+$/, "");
          if (!value) return;
          save([...origins, value]);
          setDraft("");
        }}
      >
        <div className="flex-1">
          <Field label="Add a domain" id="origin" hint="Include the scheme, e.g. https://shop.example.com">
            <Input
              id="origin"
              type="url"
              placeholder="https://shop.example.com"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </Field>
        </div>
        <Button type="submit" disabled={update.isPending}>
          Add
        </Button>
      </form>
    </Card>
  );
}
